import { DialogContentText } from "@mui/material";
import { ConfirmDialog, ResourceInfo, ResourceList } from "components/ConfirmDialog";
import pluralize from "pluralize";
import { useMemo } from "react";

type Facility = { id: string; name: string };

/**
 * A tenant group can hold thousands of facilities, so the dialog names a few and
 * counts the rest rather than rendering a row per facility.
 */
const MAX_LISTED = 10;

export function ConfirmRemoveTenant({
  tenantName,
  facilities,
  /**
   * Set when the user clicked a single facility's remove button and it happens to
   * be the last one in its tenant. The write is identical — unlinking every
   * facility in the group — but the wording has to explain the consequence they
   * didn't ask for, which is that the tenant disappears from the credential too.
   */
  lastInTenant = false,
  loading,
  onConfirm,
  onClose,
}: {
  tenantName: string;
  facilities: Facility[];
  lastInTenant?: boolean;
  loading: boolean;
  onConfirm: () => void | Promise<unknown>;
  onClose: () => void;
}) {
  const resources: ResourceInfo[] = useMemo(
    () =>
      facilities.slice(0, MAX_LISTED).map((facility) => ({
        resourceType: "Facility",
        resourceName: facility.name,
      })),
    [facilities]
  );

  const remaining = facilities.length - resources.length;

  const header = lastInTenant
    ? "Are you sure you want to remove this facility from the credential?"
    : "Are you sure you want to remove the entire tenant from the credential?";

  const explanation = lastInTenant
    ? `This is the only facility linked to ${tenantName}, so removing it also removes ${tenantName} from this credential.`
    : `This will remove ${facilities.length} ${pluralize("facility", facilities.length)} within ${tenantName}. You can also close this dialog and remove them individually.`;

  return (
    <ConfirmDialog
      open
      header={header}
      confirmText={lastInTenant ? "Yes, Remove Facility" : "Yes, Remove Tenant"}
      loading={loading}
      onConfirm={onConfirm}
      onClose={onClose}
    >
      {/* Composed rather than passed as `preResourcesInfo` + `resources`, which
          would butt the paragraph straight against the first facility row. */}
      <DialogContentText sx={{ mb: 3 }}>{explanation}</DialogContentText>
      <ResourceList resources={resources} />
      {remaining > 0 && <DialogContentText>{`…and ${remaining} more.`}</DialogContentText>}
    </ConfirmDialog>
  );
}
